// useSendEmailActionState.js

import { useMemo } from 'react';

// Utilities
import { isEmailBulletFieldOption } from 'collection/automations/utils/automationFieldUtils';
import {
  normalizeMessageConfig,
  normalizeRecipientSource,
} from '../utils/automationActionEditorUtils';

/**
 * useSendEmailActionState
 *
 * Derives normalized recipient/message state and option lists for the send-email row.
 *
 * @param {object} action - Action row payload.
 * @param {array} fieldOptions - Available fields from the rule editor.
 * @param {array} nodeTypeOptions - Available node types with people fields.
 *
 * @returns {object} Derived send-email UI state.
 */
const useSendEmailActionState = ({ action, fieldOptions, nodeTypeOptions }) => {
  // ----- UTILITIES ----- //

  const recipientSource = normalizeRecipientSource(action);
  const messageConfig = normalizeMessageConfig(action);

  // ----- MEMOS ----- //

  const emailBulletFieldOptions = useMemo(() => {
    return (Array.isArray(fieldOptions) ? fieldOptions : [])
      .filter((field) => isEmailBulletFieldOption(field));
  }, [fieldOptions]);

  const selectableFieldKeySet = useMemo(() => {
    return new Set(
      emailBulletFieldOptions
        .map((field) => String(field?.key || '').trim())
        .filter(Boolean),
    );
  }, [emailBulletFieldOptions]);

  const effectiveIncludedFieldKeys = useMemo(() => {
    const includedFieldKeys = Array.isArray(messageConfig.includedFieldKeys)
      ? messageConfig.includedFieldKeys
      : [];

    return includedFieldKeys
      .map((fieldKey) => String(fieldKey || '').trim())
      .filter((fieldKey) => selectableFieldKeySet.has(fieldKey));
  }, [messageConfig.includedFieldKeys, selectableFieldKeySet]);

  const currentPeopleFieldOptions = useMemo(() => {
    return (Array.isArray(fieldOptions) ? fieldOptions : [])
      .filter((field) => String(field?.type || '').trim().toLowerCase() === 'people');
  }, [fieldOptions]);

  const relatedPeopleFieldOptions = useMemo(() => {
    const relatedNodeType = (Array.isArray(nodeTypeOptions) ? nodeTypeOptions : [])
      .find((typeOption) => String(typeOption?.key || '').trim() === recipientSource.relatedNodeTypeKey) || null;

    return Array.isArray(relatedNodeType?.peopleFields)
      ? relatedNodeType.peopleFields
      : [];
  }, [nodeTypeOptions, recipientSource.relatedNodeTypeKey]);

  return {
    recipientSource,
    messageConfig,
    emailBulletFieldOptions,
    selectableFieldKeySet,
    effectiveIncludedFieldKeys,
    currentPeopleFieldOptions,
    relatedPeopleFieldOptions,
  };
};

export default useSendEmailActionState;
